import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import DocumentStatusBadge from './DocumentStatusBadge';
import { Checkbox } from '../../../components/ui/Checkbox';

const DocumentCard = ({
  document,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  onRestore,
  onDownload,
  filterState
}) => {
  const navigate = useNavigate();

  const getCategoryIcon = (category) => {
    const icons = {
      rams: 'FileText',
      insurance: 'Shield',
      training: 'GraduationCap',
      plant: 'Wrench'
    };
    return icons?.[category] || 'File';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No expiry';
    const date = new Date(dateString);
    if (isNaN(date?.getTime())) return 'No expiry';
    return date?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getComputedStatus = (expiryDate) => {
    if (!expiryDate) return { status: 'no-expiry', daysUntilExpiry: null };
    const date = new Date(expiryDate);
    if (isNaN(date?.getTime())) return { status: 'no-expiry', daysUntilExpiry: null };
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    const diffMs = date - today;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { status: 'expired', daysUntilExpiry: diffDays };
    if (diffDays <= 30) return { status: 'expiring-soon', daysUntilExpiry: diffDays };
    return { status: 'valid', daysUntilExpiry: diffDays };
  };

  const expiryDate = document?.expiry_date || document?.expiryDate;
  const { status, daysUntilExpiry } = getComputedStatus(expiryDate);

  const handleCardClick = () => {
    navigate(`/documents/${document?.id}`, { state: { document, filterState } });
  };

  const stopPropagation = (e) => e?.stopPropagation();

  return (
    <div
      className="bg-card border border-border rounded-lg p-4 shadow-elevation-1 hover:shadow-elevation-2 hover:border-accent/40 transition-smooth cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div onClick={stopPropagation}>
            <Checkbox
              checked={isSelected}
              onChange={(e) => onSelect(document?.id, e?.target?.checked)}
              className="mt-1"
            />
          </div>
          <div className="flex items-center justify-center w-12 h-12 bg-accent/10 rounded-lg flex-shrink-0">
            <Icon name={getCategoryIcon(document?.category)} size={24} color="var(--color-accent)" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground mb-1 line-clamp-2 hover:text-accent transition-colors">{document?.title}</h3>
            <p className="text-sm text-muted-foreground truncate">{document?.siteName || document?.sites?.site_name}</p>
          </div>
        </div>
        <DocumentStatusBadge status={status} daysUntilExpiry={daysUntilExpiry} />
      </div>
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Category:</span>
          <span className="text-sm font-medium text-foreground">{document?.categoryLabel || document?.category}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Owner:</span>
          <span className="text-sm font-medium text-foreground">{document?.owner}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Issue Date:</span>
          <span className="text-sm font-medium text-foreground whitespace-nowrap">{formatDate(document?.issueDate || document?.issue_date)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Expiry Date:</span>
          <span className="text-sm font-medium text-foreground whitespace-nowrap">{formatDate(expiryDate)}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 pt-3 border-t border-border" onClick={stopPropagation}>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDownload(document)}
          iconName="Download"
          iconPosition="left"
          className="flex-1"
        >
          Download
        </Button>
        {onEdit && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(document)}
            aria-label="Edit document"
          >
            <Icon name="Edit" size={18} />
          </Button>
        )}
        {onDelete && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(document)}
            aria-label="Delete document"
          >
            <Icon name="Trash2" size={18} className="text-destructive" />
          </Button>
        )}
        {onRestore && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onRestore(document)}
            aria-label="Restore document"
          >
            <Icon name="RotateCcw" size={18} className="text-accent" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default DocumentCard;